import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DOCUMENT,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OutputEmitterRef,
  OutputRefSubscription,
  viewChild
} from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { IModalButton, IModalData, ModalService } from '../../services/modal.service';

// Elements Tab can reach inside the modal
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

let nextId = 0;

@Component({
  selector: 'ui-modal',
  imports: [
    NgComponentOutlet
  ],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
  host: {
    '[class.modal-fullscreen]': 'data()?.styles?.fullscreen',
    '[class.modal-sidebar]': 'data()?.styles?.sidebar',
    '[class.no-backdrop]': 'data()?.styles?.noBackdrop',
    '[style.--fullscreen-top.px]': 'data()?.styles?.fullscreenTop || null',
    '(document:keydown)': 'onKeydown($event)',
    'animate.enter': 'modal-overlay-enter',
    'animate.leave': 'modal-overlay-leave',
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalComponent implements AfterViewInit, OnDestroy {
  readonly ngComponentOutlet = viewChild(NgComponentOutlet);
  readonly data = input<IModalData | null>(null);
  protected readonly titleId = `ui-modal-title-${nextId++}`;
  private readonly dialog = viewChild.required<ElementRef<HTMLElement>>('dialog');
  private readonly document = inject(DOCUMENT);
  private modal = inject(ModalService);
  private subscriptions: OutputRefSubscription[] = [];
  // Element that opened the modal, it gets the focus back on close
  private opener: Element | null = null;

  constructor() {
    afterNextRender(() => {
      this.opener = this.document.activeElement;
      this.dialog().nativeElement.focus();
    });
  }

  ngAfterViewInit() {
    const ngComponentOutlet = this.ngComponentOutlet();
    if (ngComponentOutlet && ngComponentOutlet['_componentRef']?.instance) {
      ngComponentOutlet['_componentRef'].instance._modal = this;
      this.bindOutputs(ngComponentOutlet['_componentRef']);
    }
  }

  onButtonClick(btn: IModalButton, e: Event) {
    if (btn.click) {
      btn.click(btn, e as MouseEvent, this.ngComponentOutlet()?.componentInstance);
    }
    const data = this.data();
    if (btn.close && data) {
      this.modal.close(data);
    }
  }

  close() {
    const data = this.data();
    if (!data) {
      return;
    }
    this.modal.close(data);
  }

  // Only the top modal handles the keyboard: Tab stays inside it, Esc works like its cancel button
  protected onKeydown(e: KeyboardEvent) {
    if (this.modal.modals().at(-1) !== this.data()) {
      return;
    }
    if (e.key === 'Escape') {
      this.escape(e);
    } else if (e.key === 'Tab') {
      this.keepFocusInside(e);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
    if (this.opener instanceof HTMLElement && this.opener.isConnected) {
      this.opener.focus();
    }
  }

  // A closing button that isn't primary ("Cancel", "No"), or the only button if it closes ("Close").
  // A modal without buttons closes, a modal whose buttons don't close stays open
  private escape(e: KeyboardEvent) {
    const buttons = this.data()?.buttons ?? [];
    const btn = buttons.find(b => b.close && !b.primary) ?? (buttons.length === 1 && buttons[0].close ? buttons[0] : undefined);
    if (btn) {
      e.preventDefault();
      this.onButtonClick(btn, e);
    } else if (!buttons.length) {
      e.preventDefault();
      this.close();
    }
  }

  private keepFocusInside(e: KeyboardEvent) {
    const dialog = this.dialog().nativeElement;
    const items = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(el => el.getClientRects().length);
    if (!items.length) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = this.document.activeElement;
    if (!dialog.contains(active) || active === dialog) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  private bindOutputs(comp: ComponentRef<any>) {
    const outputs = this.data()?.outputs;
    if (!outputs) {
      return;
    }
    for (const key in outputs) {
      if (comp.instance.hasOwnProperty(key) && (comp.instance[key] instanceof OutputEmitterRef)) {
        const sub = (comp.instance[key] as OutputEmitterRef<any>).subscribe((...args) => outputs[key](...args));
        this.subscriptions.push(sub);
      }
    }
  }
}
